/**
 * ConnectLinkedInModal — Browserbase-powered LinkedIn connection flow
 *
 * Embeds a remote browser session for LinkedIn login, auto-detects
 * when login completes, and stores credentials for the author.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, Loader2, CheckCircle2, AlertTriangle, ExternalLink, RefreshCw } from 'lucide-react';
import { usePollLinkedInLogin } from '../../hooks/useLinkedInStatus';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ConnectLinkedInModalProps {
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
  authorId: string;
  workspaceId: string;
}

type ConnectionPhase = 'idle' | 'initializing' | 'waiting' | 'detecting' | 'connected' | 'error';

// ─── Component ───────────────────────────────────────────────────────────────

export function ConnectLinkedInModal({
  open,
  onClose,
  onConnected,
  authorId,
  workspaceId,
}: ConnectLinkedInModalProps) {
  const [phase, setPhase] = useState<ConnectionPhase>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [sessionUrl, setSessionUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Poll for login completion
  const { data: pollData, isPolling, error: pollError } = usePollLinkedInLogin(
    phase === 'waiting' || phase === 'detecting' ? sessionId : null,
    authorId,
    workspaceId,
  );

  // Handle poll result
  useEffect(() => {
    if (pollData?.isComplete) {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the compiler lint only sees this effect now that the guarded bodies are gone
      setPhase('connected');
      onConnected?.();
    }
  }, [pollData, onConnected]);

  // Handle poll error
  useEffect(() => {
    if (pollError) {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the compiler lint only sees this effect now that the guarded bodies are gone
      setErrorMessage(pollError);
    }
  }, [pollError]);

  // Initialize Browserbase session
  const startSession = useCallback(async () => {
    setErrorMessage('LinkedIn publishing is not enabled in this environment.');
    setPhase('error');
  }, []);

  // Auto-start on open
  useEffect(() => {
    if (open && phase === 'idle') {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the compiler lint only sees this effect now that the guarded bodies are gone
      startSession();
    }
  }, [open, phase, startSession]);

  // Cleanup on close
  const handleClose = useCallback(() => {
    setPhase('idle');
    setSessionId(null);
    setSessionUrl(null);
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Connect LinkedIn</h2>
              <p className="text-sm text-zinc-500">Sign in to your LinkedIn account to enable publishing</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Content */}
        <div className="relative" style={{ height: '560px' }}>
          {/* Initializing */}
          {phase === 'initializing' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              <p className="text-zinc-600 dark:text-zinc-400">Starting secure browser session...</p>
            </div>
          )}

          {/* Waiting — show iframe */}
          {(phase === 'waiting' || phase === 'detecting') && sessionUrl && (
            <>
              <iframe
                ref={iframeRef}
                src={sessionUrl}
                className="w-full h-full border-0"
                allow="clipboard-write"
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              />
              {isPolling && (
                <div className="absolute bottom-0 left-0 right-0 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 border-t border-blue-200 dark:border-blue-800 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-700 dark:text-blue-300">
                    Waiting for you to sign in to LinkedIn...
                  </span>
                </div>
              )}
            </>
          )}

          {/* Connected */}
          {phase === 'connected' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                LinkedIn Connected
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 text-center max-w-md">
                Your LinkedIn account is now connected. You can publish and manage posts directly from Martha.
              </p>
              <button
                onClick={handleClose}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Done
              </button>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
                Connection Failed
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 text-center max-w-md">
                {errorMessage || 'Something went wrong. Please try again.'}
              </p>
              <div className="flex gap-3 mt-4">
                <button
                  onClick={startSession}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-zinc-300 dark:border-zinc-600 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer — security note */}
        <div className="px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50">
          <p className="text-xs text-zinc-500 flex items-center gap-1.5">
            <ExternalLink className="w-3 h-3" />
            Secure session powered by Browserbase. Your credentials are encrypted and stored locally.
          </p>
        </div>
      </div>
    </div>
  );
}
